import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'

import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { PlanVersionSummary } from 'app/frontend/base/api/plan_versions'

dayjs.extend(RelativeTime)

export type PlanVersionPreviewControlsProps = {
  summary?: PlanVersionSummary
  versionNumber?: number
  isLoading: boolean
  canEdit: boolean
  canStepOlder: boolean
  canStepNewer: boolean
  isRestoring: boolean
  onStep: (delta: number) => void
  onRestore: () => void | Promise<void>
  onCancel: () => void
}

export const PlanVersionPreviewControls = ({
  summary,
  versionNumber,
  canEdit,
  canStepOlder,
  canStepNewer,
  isRestoring,
  onStep,
  onRestore,
  onCancel,
}: PlanVersionPreviewControlsProps) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const timeLabel = summary
    ? dayjs(summary.createdAt).fromNow()
    : t('shared.loading')

  return (
    <>
      <div className='flex flex-row items-center gap-3'>
        <Button
          variant='outline'
          size='icon'
          onClick={() => onStep(1)}
          disabled={!canStepOlder || isRestoring}
        >
          <ChevronLeft className='h-4 w-4' />
        </Button>
        <div className='min-w-32 text-center'>
          <div className='text-sm font-medium leading-tight'>
            {t('dashboard.team.plans.versions.previewing', {
              number: versionNumber,
            })}
          </div>
          <div className='text-xs text-muted-foreground leading-tight'>
            {timeLabel}
          </div>
        </div>
        <Button
          variant='outline'
          size='icon'
          onClick={() => onStep(-1)}
          disabled={!canStepNewer || isRestoring}
        >
          <ChevronRight className='h-4 w-4' />
        </Button>
        {canEdit ? (
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={isRestoring || !summary}
          >
            {t('dashboard.team.plans.versions.restore')}
          </Button>
        ) : null}
        <Button variant='outline' onClick={onCancel} disabled={isRestoring}>
          {t('shared.cancel')}
        </Button>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        description={t('dashboard.team.plans.versions.restore-confirm')}
        continueText={t('dashboard.team.plans.versions.restore')}
        loading={isRestoring}
        onCancel={() => setConfirmOpen(false)}
        onContinue={async () => {
          await onRestore()
          setConfirmOpen(false)
        }}
      />
    </>
  )
}

type Props = PlanVersionPreviewControlsProps & {
  open: boolean
}

const PlanVersionPreviewBar = ({ open, ...controls }: Props) => {
  const reduceMotion = useReducedMotion()

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className='fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 pointer-events-none'
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 80 }}
          transition={
            reduceMotion
              ? { duration: 0.15 }
              : { type: 'spring', stiffness: 460, damping: 22 }
          }
        >
          <Card size='sm' className='pointer-events-auto shadow-lg'>
            <PlanVersionPreviewControls {...controls} />
          </Card>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export default PlanVersionPreviewBar
